import type { ApiOrgContext } from "./core";
import { createOrgQuery } from "./hook-factory";

type OrgContextArg = ApiOrgContext | string | null | undefined;

/**
 * Workspace `search_memory` (the same tool agents call). Used by the command
 * palette so typing in Cmd+K recalls entities, connections, and events instead
 * of filtering the events feed.
 */

export type SearchMemoryEntity = {
	id: number;
	type: string;
	name: string;
	slug: string;
	parent_id: number | null;
	parent_name: string | null;
	parent_slug: string | null;
	parent_entity_type: string | null;
	organization_slug: string | null;
	workspace_slug: string | null;
};

export type SearchMemoryContent = {
	id: number;
	title: string | null;
	// `search_memory` may return nulls here for system rows (e.g. a
	// "Connection needs authorization" event has no author/platform/source).
	// The row renderer must handle them — see `flattenSearchMemoryHits`.
	text_content: string | null;
	platform: string | null;
	workspace_slug: string | null;
};

export type SearchMemoryConnection = {
	connection_id: number;
	connector_key: string | null;
	display_name: string | null;
	workspace_slug: string | null;
};

export type SearchMemoryResult = {
	entity: SearchMemoryEntity | null;
	matches: SearchMemoryEntity[];
	connections?: SearchMemoryConnection[];
	content?: SearchMemoryContent[];
};

const MIN_QUERY_LENGTH = 2;

export const useSearchMemory = createOrgQuery<
	[query: string, orgContext?: OrgContextArg],
	SearchMemoryResult
>({
	queryKey: (ctx, query) => ["search-memory", ctx.organizationId, ctx.slug, query],
	tool: "search_memory",
	body: (query) => ({
		query,
		limit: 8,
		content_limit: 5,
		include_content: true,
		include_connections: true,
		include_public_catalogs: false,
	}),
	orgContext: (_query, orgContext) => orgContext,
	enabled: (ctx, query) =>
		query.trim().length >= MIN_QUERY_LENGTH &&
		!!(ctx.organizationId || ctx.slug),
	staleTime: 15_000,
});
